import { useTransportationClient } from '@frontend/clients/transportation.request'
import { useClearAllPurchaseOrdersCache } from '@frontend/queries/purchase-order'
import {
  useClearAllShipmentDetailsCache,
  useClearShipmentSummariesCache,
} from '@frontend/queries/shipments'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  TransportationCreationPayload,
  TransportationShipmentCreationPayload,
  TransportationShipmentsUpdatePayload,
  TransportationStatusUpdatePayload,
  TransportationUpdatePayload,
} from '@shared-types/transportations'

export const useClearTransportationCache = () => {
  const queryClient = useQueryClient()
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: ['transportation_list'],
    })
  }
}

export const useMutateCreateTransportation = () => {
  const transportationClient = useTransportationClient()
  const clearTransportationCache = useClearTransportationCache()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()
  const clearShipmentSummariesCache = useClearShipmentSummariesCache()

  return useMutation({
    mutationFn: (payload: TransportationCreationPayload) => transportationClient.create(payload),
    onSuccess: async () => {
      await clearTransportationCache()
      await clearAllShipmentDetailsCache()
      await clearShipmentSummariesCache()
    },
  })
}

type UseMutateUpdateTransportation = {
  transportationId: string
  payload: TransportationUpdatePayload
}
export const useMutateUpdateTransportation = () => {
  const transportationClient = useTransportationClient()
  const clearTransportationCache = useClearTransportationCache()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()

  return useMutation({
    mutationFn: ({ transportationId, payload }: UseMutateUpdateTransportation) =>
      transportationClient.update(transportationId, payload),
    onSuccess: async () => {
      await clearTransportationCache()
      await clearAllShipmentDetailsCache()
    },
  })
}

type UseMutateUpdateTransportationShipment = {
  transportationId: string
  payload: TransportationShipmentsUpdatePayload
}
export const useMutateUpdateTransportationShipments = () => {
  const transportationClient = useTransportationClient()
  const clearTransportationCache = useClearTransportationCache()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()

  return useMutation({
    mutationFn: ({ transportationId, payload }: UseMutateUpdateTransportationShipment) =>
      transportationClient.updateShipments(transportationId, payload),
    onSuccess: async () => {
      await clearTransportationCache()
      await clearAllShipmentDetailsCache()
    },
  })
}

type UseMutateUpdateTransportationStatus = {
  transportationId: string
  payload: TransportationStatusUpdatePayload
}
export const useMutateUpdateTransportationStatus = () => {
  const transportationClient = useTransportationClient()
  const clearTransportationCache = useClearTransportationCache()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()

  return useMutation({
    mutationFn: ({ transportationId, payload }: UseMutateUpdateTransportationStatus) =>
      transportationClient.updateStatus(transportationId, payload),
    onSuccess: async () => {
      await clearTransportationCache()
      await clearAllShipmentDetailsCache()
    },
  })
}

type UseMutateCreateTransportationShipment = {
  transportationId: string
  payload: TransportationShipmentCreationPayload
}
export const useMutateCreateTransportationShipment = () => {
  const transportationClient = useTransportationClient()
  const clearTransportationCache = useClearTransportationCache()
  const clearAllShipmentDetailsCache = useClearAllShipmentDetailsCache()
  const clearShipmentSummariesCache = useClearShipmentSummariesCache()
  const clearPurchaseOrdersCache = useClearAllPurchaseOrdersCache()

  return useMutation({
    mutationFn: ({ transportationId, payload }: UseMutateCreateTransportationShipment) =>
      transportationClient.createShipment(transportationId, payload),
    onSuccess: async () => {
      await clearTransportationCache()
      await clearAllShipmentDetailsCache()
      await clearShipmentSummariesCache()
      await clearPurchaseOrdersCache()
    },
  })
}
